import classNames from "classnames";
import noop from "lodash/noop";
import React, {
  CSSProperties,
  FunctionComponent,
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import "./style.scss";

export type Color = "grey" | "blue";

interface Props {
  children: ReactNode;
  className?: string;
  color?: Color;
  onMouseUp?: () => void;
  style?: CSSProperties;
}

interface RippleStyle {
  height?: string;
  left?: string;
  top?: string;
  width?: string;
}

interface PropsRippleSpan {
  rippleStyle: RippleStyle;
  color: string;
}

const RippleSpan: FunctionComponent<PropsRippleSpan> = React.memo(
  ({ rippleStyle = {}, color }): ReactElement => {
    const spanArray = Object.values(rippleStyle);
    return (
      <>
        {spanArray.map((values: RippleStyle, index) => (
          <span
            className={classNames("Ripple-effect", `Ripple-effect--${color}`)}
            key={`spanCount_${index}`}
            style={{ ...values }}
          />
        ))}
      </>
    );
  }
);

const Ripple: FunctionComponent<Props> = ({
  children,
  className = "",
  style = {},
  color = "grey",
  onMouseUp = noop,
}): ReactElement => {
  const [params, setParams] = useState<{ count: number; rippleStyle: RippleStyle }>({
    count: 0,
    rippleStyle: {},
  });

  const [bounceID, setBounceID] = useState<number>();

  const handleMouseUp = useCallback(() => {
    clearTimeout(bounceID);
    // use window.setTimeout to resolve `node/typings`
    const id = window.setTimeout(() => {
      setParams({
        count: 0,
        rippleStyle: {},
      });
    }, 2000);
    onMouseUp();
    setBounceID(id);
  }, [bounceID, onMouseUp]);

  useEffect(() => {
    return () => {
      clearTimeout(bounceID);
    };
  }, [bounceID]);

  const showRipple = React.useCallback(
    (e: MouseEvent) => {
      const rippleContainer = e.currentTarget;
      const pos = rippleContainer.getBoundingClientRect();
      const size = pos.width;
      const x = e.pageX - pos.left - size / 2 - window.scrollX;
      const y = e.pageY - pos.top - size / 2 - window.scrollY;

      const newRippleStyle = {
        height: `${size}px`,
        left: `${x}px`,
        top: `${y}px`,
        width: `${size}px`,
      };

      setParams(({ count, rippleStyle }) => ({
        count: count + 1,
        rippleStyle: { ...rippleStyle, [count + 1]: newRippleStyle },
      }));
    },
    [setParams]
  );

  return (
    <span
      className={classNames("Ripple", className)}
      onMouseDown={showRipple}
      onMouseUp={handleMouseUp}
      style={style}
    >
      {children}
      <RippleSpan color={color} rippleStyle={params.rippleStyle} />
    </span>
  );
};

export default React.memo(Ripple);
